"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense } from "react";
import { createClient } from "@/lib/supabase/client";
import { type ExamType, EXAM_CONFIGS } from "@/lib/exam/config";
import { WifiOff, GraduationCap } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ExamDef {
  id: number;
  exam_type: string;
  created_at: string;
}

interface SectionBreakdownRow {
  id: string;
  label: string;
  correct: number;
  total: number;
  score_pct: number;
}

interface FeedbackReport {
  section_breakdown?: SectionBreakdownRow[];
  // Other fields exist (question_review, improvement_tips, etc.) but the
  // practice page only needs section_breakdown for the section pills.
  [key: string]: unknown;
}

interface Session {
  id: string;
  exam_definition_id: number;
  exam_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  scored_at: string | null;
  reading_score: number | null;
  listening_score: number | null;
  speaking_score: number | null;
  writing_score: number | null;
  total_score: number | null;
  feedback_report: FeedbackReport | null;
}

/* ------------------------------------------------------------------ */
/*  Small helpers                                                      */
/* ------------------------------------------------------------------ */

function ScorePill({ score, max, examType }: { score: number | null; max: number; examType?: string }) {
  if (score == null) return <span className="text-gray-300 text-sm">—</span>;
  // IELTS stores scores band×10 in DB (e.g. 65 → 6.5); convert before display + pct
  // IELTS and GRE-AWA store scores ×10 in the DB integer column; convert for display
  const isScaledTen = examType === "ielts" || (examType === "gre" && max === 6);
  const displayVal = isScaledTen ? score / 10 : score;
  const pct = displayVal / max;
  const color =
    pct >= 0.8
      ? "text-green-600"
      : pct >= 0.6
        ? "text-blue-600"
        : pct >= 0.4
          ? "text-yellow-600"
          : "text-red-500";
  const display = isScaledTen ? displayVal.toFixed(1) : score;
  return <span className={`font-semibold text-sm ${color}`}>{display}</span>;
}

function AttemptStatusBadge({
  session,
  scoringIds,
}: {
  session: Session;
  scoringIds: Set<string>;
}) {
  if (scoringIds.has(session.id))
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
        Scoring…
      </span>
    );
  if (session.scored_at)
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
        Scored
      </span>
    );
  if (session.status === "completed")
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">
        Needs scoring
      </span>
    );
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      In progress
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Color maps — we cannot interpolate Tailwind classes so we map them */
/* ------------------------------------------------------------------ */

const COLOR_MAP: Record<string, { text: string; bg: string; active: string; border: string; lightBg: string }> = {
  blue:   { text: "text-blue-600",   bg: "bg-blue-50",   active: "bg-blue-600",   border: "border-blue-200", lightBg: "bg-blue-50"  },
  green:  { text: "text-green-600",  bg: "bg-green-50",  active: "bg-green-600",  border: "border-green-200", lightBg: "bg-green-50" },
  purple: { text: "text-purple-600", bg: "bg-purple-50", active: "bg-purple-600", border: "border-purple-200", lightBg: "bg-purple-50" },
  indigo: { text: "text-indigo-600", bg: "bg-indigo-50", active: "bg-indigo-600", border: "border-indigo-200", lightBg: "bg-indigo-50" },
  orange: { text: "text-orange-600", bg: "bg-orange-50", active: "bg-orange-600", border: "border-orange-200", lightBg: "bg-orange-50" },
};

function getColors(color: string) {
  return COLOR_MAP[color] ?? COLOR_MAP.blue;
}

/* ------------------------------------------------------------------ */
/*  Section column helpers per exam type                               */
/* ------------------------------------------------------------------ */

/**
 * Section column descriptor.
 *
 * Custom-tier exams (TOEFL/SAT/ACT/IELTS/GRE) persist section scores into the
 * legacy `{reading,listening,writing,speaking}_score` columns on
 * `exam_sessions` — those use `source: "column"` with the column name.
 *
 * Generic-tier exams (~32 exam types in `lib/exam/config.ts`) only persist the
 * total in `total_score`; per-section scores live in
 * `feedback_report.section_breakdown[].score_pct` as percentages 0–100. Those
 * use `source: "breakdown"` with the section id and a fixed `scoreMax: 100`.
 */
type SectionCol =
  | { source: "column"; key: keyof Session; label: string; scoreMax: number }
  | { source: "breakdown"; sectionId: string; label: string; scoreMax: number };

function getSectionColumns(examType: ExamType): SectionCol[] {
  const config = EXAM_CONFIGS[examType];
  switch (examType) {
    case "toefl":
      return [
        { source: "column", key: "reading_score",   label: "R", scoreMax: 30 },
        { source: "column", key: "listening_score", label: "L", scoreMax: 30 },
        { source: "column", key: "speaking_score",  label: "S", scoreMax: 30 },
        { source: "column", key: "writing_score",   label: "W", scoreMax: 30 },
      ];
    case "sat":
      return [
        { source: "column", key: "reading_score",   label: "R&W",  scoreMax: 800 },
        { source: "column", key: "listening_score", label: "Math", scoreMax: 800 },
      ];
    case "act":
      // reading_score→English, listening_score→Math, writing_score→Reading, speaking_score→Science
      return [
        { source: "column", key: "reading_score",   label: "E", scoreMax: 36 },
        { source: "column", key: "listening_score", label: "M", scoreMax: 36 },
        { source: "column", key: "writing_score",   label: "R", scoreMax: 36 },
        { source: "column", key: "speaking_score",  label: "S", scoreMax: 36 },
      ];
    case "ielts":
      // reading_score→Reading band×10, writing_score→Writing band×10
      // scoreMax 9 here because ScorePill divides by 10 before comparing
      return [
        { source: "column", key: "reading_score", label: "R", scoreMax: 9 },
        { source: "column", key: "writing_score", label: "W", scoreMax: 9 },
      ];
    case "gre":
      // reading_score→Verbal, listening_score→Quant, writing_score→AWA×10
      return [
        { source: "column", key: "reading_score",   label: "V",   scoreMax: 170 },
        { source: "column", key: "listening_score", label: "Q",   scoreMax: 170 },
        { source: "column", key: "writing_score",   label: "AWA", scoreMax: 6 },
      ];
    default:
      // Generic-tier exams: read from feedback_report.section_breakdown.
      // score_pct is 0–100 so scoreMax is fixed at 100 regardless of the
      // exam's nominal scoreMax (which may be 50, 200, etc.).
      return config.sections.map((s) => ({
        source: "breakdown" as const,
        sectionId: s.id,
        label: s.label.charAt(0),
        scoreMax: 100,
      }));
  }
}

function getSessionSectionScore(session: Session, col: SectionCol): number | null {
  if (col.source === "column") {
    const v = session[col.key];
    return typeof v === "number" ? v : null;
  }
  // breakdown — look up the section by id in feedback_report.section_breakdown
  const breakdown = session.feedback_report?.section_breakdown;
  if (!breakdown) return null;
  const row = breakdown.find((r) => r.id === col.sectionId);
  return row ? Math.round(row.score_pct) : null;
}

/**
 * IELTS stores scores ×10 in the DB (e.g. band 6.5 → 65) so they fit integer
 * columns. Convert back to display value (6.5) before showing to the user.
 * All other exams store the raw display value directly.
 */
function formatTotalScore(examType: string, rawScore: number): string {
  if (examType === "ielts") return (rawScore / 10).toFixed(1);
  return String(rawScore);
}

/** Ratio for colour-coding best-score percentages (0–1). */
function scoreRatio(examType: string, rawScore: number, scoreMax: number): number {
  if (examType === "ielts") return rawScore / (scoreMax * 10);
  return rawScore / scoreMax;
}

/* ------------------------------------------------------------------ */
/*  Main content component (inside Suspense)                           */
/* ------------------------------------------------------------------ */

interface PracticePageProps {
  examType: ExamType;
  embedded?: boolean; // When true, removes outer wrapper and back link (for hub layout)
}

/**
 * Reads ?scoring=<sessionId> and reports it upward. Isolated in its own
 * component because useSearchParams() suspends — when it lived at the top
 * of PracticePageContent, the WHOLE hub page sat inside the Suspense
 * boundary, and the Turbopack dev server intermittently never resolved it,
 * leaving hubs stuck on "Loading…". Now only this invisible bridge
 * suspends; the page renders immediately.
 */
function ScoringParamBridge({ onParam }: { onParam: (v: string | null) => void }) {
  const searchParams = useSearchParams();
  const v = searchParams.get("scoring");
  useEffect(() => { onParam(v); }, [v, onParam]);
  return null;
}

function PracticePageContent({ examType, embedded, scoringParam }: PracticePageProps & { scoringParam: string | null }) {
  const config = EXAM_CONFIGS[examType];
  const colors = getColors(config.color);
  const sectionCols = getSectionColumns(examType);

  const [exams, setExams] = useState<ExamDef[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);
  const [loading, setLoading] = useState(true);
  // CF-19: previously a network failure during the hub's initial load
  // left users staring at a spinner forever with a "Load failed" event
  // in the console. Now we capture the error in state and show a
  // retry button (see render block below).
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [starting, setStarting] = useState<number | null>(null);
  const [scoring, setScoring] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const scoringIds = new Set([
    ...Object.keys(scoring).filter((id) => scoring[id]),
    ...(scoringParam ? [scoringParam] : []),
  ]);

  /* ---- Load sessions helper ---- */
  const loadSessions = useCallback(
    async (userId: string) => {
      const supabase = createClient();
      const { data } = await supabase
        .from("exam_sessions")
        .select(
          "id, exam_definition_id, exam_type, status, started_at, completed_at, scored_at, reading_score, listening_score, speaking_score, writing_score, total_score, feedback_report"
        )
        .eq("user_id", userId)
        .eq("exam_type", examType)
        .order("started_at", { ascending: false })
        .limit(200);
      if (data) setSessions(data);
    },
    [examType]
  );

  /* ---- Initial load: auth + exams + sessions ---- */
  useEffect(() => {
    const supabase = createClient();
    async function load() {
      // CF-19: wrap the entire load in try/catch so a single failed
      // fetch (transient network, Safari "Load failed", etc.) doesn't
      // leave the hub stuck on the spinner. Errors surface in
      // `loadError`; the user gets a Retry button (see render block).
      try {
        setLoadError(null);
        const {
          data: { user: u },
        } = await supabase.auth.getUser();

        // Load exams publicly — no auth required to see the list
        const examsRes = await fetch(`/api/exams?type=${examType}`);
        if (!examsRes.ok) {
          throw new Error(`Couldn't load exam list (HTTP ${examsRes.status}). Check your connection and retry.`);
        }
        const examsData: ExamDef[] = (await examsRes.json()) ?? [];
        setExams(examsData);

        if (u) {
          const { data: userData } = await supabase
            .from("users")
            .select("id, email, display_name")
            .eq("id", u.id)
            .single();

          setUser(userData ?? { id: u.id, email: u.email ?? "" });
          await loadSessions(u.id);
        }

        setLoading(false);
      } catch (err) {
        const message = err instanceof Error ? err.message :
          "Couldn't load this page. Check your connection and retry.";
        setLoadError(message);
        setLoading(false);
      }
    }
    load();
    // `reloadCounter` is the retry trigger — bumping it re-runs the effect
  }, [examType, loadSessions, reloadCounter]);

  /* ---- Auto-expand exam row when scoring param present ---- */
  useEffect(() => {
    if (!scoringParam || exams.length === 0 || sessions.length === 0) return;
    const session = sessions.find((s) => s.id === scoringParam);
    if (session)
      setExpanded((prev) => new Set([...prev, session.exam_definition_id]));
  }, [scoringParam, exams, sessions]);

  /* ---- Polling for scoring sessions (exponential backoff) ----
   * Previously this polled /api/sessions every 5s forever while ANY
   * session was scoring. That hit Supabase 12×/minute × N concurrent
   * users with a heavy SELECT (incl. feedback_report JSONB) — a real
   * source of recurring Disk IO budget burn. See
   * docs/supabase-io-audit-2026-05-27.md item #1.
   *
   * New behaviour: poll on a backoff schedule (5s, 10s, 20s, 30s, 60s,
   * then 60s thereafter) and cap total wall-clock at 5 minutes. Most
   * scores complete in <30s so the first 3 polls do the real work; the
   * tail protects us if scoring hangs without making us pay forever.
   *
   * The effect re-arms whenever `sessions` updates — so when a session's
   * scored_at flips, `hasPending` goes false and we naturally stop.
   */
  useEffect(() => {
    if (!user) return;
    const hasPending = sessions.some(
      (s) => scoringIds.has(s.id) && !s.scored_at
    );
    if (!hasPending && !scoringParam) return;

    const POLL_SCHEDULE_MS = [5000, 10000, 20000, 30000, 60000];
    const MAX_TOTAL_MS = 5 * 60 * 1000; // 5 minutes
    const startedAt = Date.now();
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_TOTAL_MS) return; // cap reached
      const delay =
        POLL_SCHEDULE_MS[Math.min(attempt, POLL_SCHEDULE_MS.length - 1)];
      attempt += 1;
      timer = setTimeout(async () => {
        if (cancelled) return;
        await loadSessions(user.id);
        scheduleNext();
      }, delay);
    };

    scheduleNext();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sessions, scoringParam, loadSessions]);

  /* ---- Actions ---- */
  async function startExam(examDefId: number) {
    // Gate auth at the point of starting — not at page load
    if (!user) {
      window.location.href = `/auth?mode=signup&redirect=/${examType}`;
      return;
    }
    setStarting(examDefId);
    setError(null);
    // Guarded: a network blip here used to leave the Start button spinning
    // forever with an unhandled rejection.
    try {
      const res = await fetch("/api/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exam_definition_id: examDefId, exam_type: examType }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStarting(null);
        setError(data.message ?? data.error ?? "Failed to start exam");
        return;
      }
      window.location.href = `/${examType}/exam/${data.session_id}`;
    } catch {
      setStarting(null);
      setError("Network error while starting the exam. Please check your connection and try again.");
    }
  }

  async function scoreSession(sessionId: string) {
    setScoring((prev) => ({ ...prev, [sessionId]: true }));
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/score`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as Record<string, string>;
        setError(body.error ?? body.message ?? `Scoring failed (${res.status}). Please try again.`);
      } else {
        await loadSessions(user!.id);
      }
    } catch {
      setError("Network error while scoring. Please check your connection and try again.");
    } finally {
      setScoring((prev) => ({ ...prev, [sessionId]: false }));
    }
  }

  async function deleteSession(sessionId: string) {
    if (!confirm("Delete this attempt? This cannot be undone.")) return;
    setDeletingId(sessionId);
    try {
      const res = await fetch(`/api/sessions/${sessionId}`, { method: "DELETE" });
      if (res.ok) setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      else setError("Couldn't delete the attempt. Please try again.");
    } catch {
      setError("Network error while deleting. Please check your connection and try again.");
    } finally {
      setDeletingId(null);
    }
  }

  function toggleExpanded(examId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(examId) ? next.delete(examId) : next.add(examId);
      return next;
    });
  }

  /* ---- Derived stats ---- */
  const tabSessions = sessions; // already filtered by examType in query
  const scoredSessions = tabSessions.filter((s) => s.scored_at);
  const bestScore = scoredSessions.reduce<number | null>(
    (best, s) =>
      s.total_score != null && (best == null || s.total_score > best)
        ? s.total_score
        : best,
    null
  );
  const avgScore =
    scoredSessions.length > 0
      ? Math.round(
          scoredSessions.reduce((sum, s) => sum + (s.total_score ?? 0), 0) /
            scoredSessions.length
        )
      : null;

  const maxScore = config.v2ScoreMax;

  /* ---- Loading state (skeleton) ---- */
  if (loading) {
    const skeleton = (
      <>
        {!embedded && <div className="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded animate-pulse mb-6" />}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-8 w-8 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
          <div>
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-800 rounded animate-pulse mb-1" />
            <div className="h-3 w-48 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
            <div className="h-3 w-20 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
          </div>
          {[1,2,3,4,5].map(i => (
            <div key={i} className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 last:border-0 flex items-center gap-4">
              <div className="h-4 w-4 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded animate-pulse mb-1" />
                <div className="h-3 w-48 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
              </div>
              <div className="h-7 w-16 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </>
    );

    if (embedded) return skeleton;
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {skeleton}
        </div>
      </div>
    );
  }

  /* ---- Load error — CF-19: show retry instead of stuck spinner ---- */
  if (loadError) {
    const errorContent = (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-red-200 dark:border-red-900 p-8 text-center">
        <div className="mb-3"><WifiOff className="w-6 h-6 text-red-400 inline-block" strokeWidth={1.75} aria-hidden="true" /></div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
          Couldn&apos;t load this page
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-5 max-w-md mx-auto">
          {loadError}
        </p>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            setLoadError(null);
            setReloadCounter((n) => n + 1);
          }}
          className="text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-5 py-2.5 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
        >
          Retry
        </button>
      </div>
    );
    if (embedded) return errorContent;
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-6 py-8">{errorContent}</div>
      </div>
    );
  }

  /* ---- Unavailable exam — coming soon ---- */
  if (!config.available) {
    const unavailableContent = (
      <>
        {!embedded && (
          <Link
            href="/dashboard"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
          >
            ← Back to Dashboard
          </Link>
        )}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <div className="mb-4"><config.icon className={`w-6 h-6 inline-block ${colors.text}`} strokeWidth={1.75} aria-hidden="true" /></div>
          <div className="inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 mb-3">
            Coming Soon
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {config.name} Practice Exams
          </h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            We're holding off on full-length {config.name} practice exams
            until we can ship them at the same quality bar as our flagship
            exams (TOEFL, SAT, GRE, IELTS, ACT). In the meantime, the
            preparation resources below are free to use.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              href={`/${examType}/guide`}
              className="text-sm bg-gray-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              {config.name} Guide →
            </Link>
            <Link
              href={`/${examType}/vocabulary`}
              className="text-sm bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Vocabulary →
            </Link>
            <Link
              href={`/${examType}/scoring-guide`}
              className="text-sm bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Scoring Guide →
            </Link>
          </div>
        </div>
      </>
    );

    if (embedded) return unavailableContent;
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {unavailableContent}
        </div>
      </div>
    );
  }

  /* ---- Available exam — full practice page ---- */

  // Inline style for attempt row grid template (Tailwind can't interpolate arbitrary
  // class names at build time, so we use inline styles instead)
  const sectionColCount = sectionCols.length;
  const attemptGridTemplate =
    examType === "sat"
      ? "1fr 90px 70px 70px 80px 110px"
      : `1fr 90px repeat(${sectionColCount}, 50px) 80px 110px`;

  const mainContent = (
    <>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <config.icon className={`w-6 h-6 flex-shrink-0 ${colors.text}`} strokeWidth={1.75} aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 flex-wrap">
              {config.name} Practice
              {config.status === "beta" && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 align-middle"
                  title="Beta — content is functional but one polish layer (TTS audio, custom scale, etc.) is still in progress. See the readiness doc for details."
                >
                  Beta
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500">
              {config.sections.map((s) => s.label).join(" · ")}
            </p>
          </div>
        </div>

        {/* Beta caveat — shown above the exam list for beta-tier exams */}
        {config.status === "beta" && (
          <div className="mb-6 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
            <span className="font-semibold">Beta:</span> {config.name} practice is functional but
            still being polished — typically missing TTS audio for listening
            sections or a section-specific score scale. Your scores are saved
            and accurate; the exam-runner experience will improve over the
            coming weeks.
          </div>
        )}

        {/* Stats bar */}
        {scoredSessions.length > 0 && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[
              { label: "Exams taken", value: tabSessions.length },
              { label: "Scored", value: scoredSessions.length },
              {
                label: "Best score",
                value:
                  bestScore != null
                    ? `${formatTotalScore(examType, bestScore)}${config.scoreLabel}`
                    : "—",
              },
              {
                label: "Average",
                value:
                  avgScore != null
                    ? `${formatTotalScore(examType, avgScore)}${config.scoreLabel}`
                    : "—",
              },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4"
              >
                <div className="text-xs text-gray-400 mb-1">{s.label}</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Progress bar */}
        {exams.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">
                {scoredSessions.length} of {exams.length} scored
              </span>
              <span className="text-xs text-gray-400">
                {tabSessions.length} attempted
              </span>
            </div>
            <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full ${colors.active} rounded-full transition-all duration-500`}
                style={{ width: `${(scoredSessions.length / exams.length) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Exam list */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          {/* Table header */}
          {/* On <640px (mobile), drop Attempts + Best columns so the name has
              breathing room. Two-column layout matches what the row already
              does on mobile via `flex sm:grid`. */}
          <div className="hidden sm:grid grid-cols-[1fr_80px_80px_140px] gap-4 px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
            <div className="text-xs font-medium text-gray-400">
              Practice Exam
            </div>
            <div className="text-xs font-medium text-gray-400 text-center">
              Attempts
            </div>
            <div className="text-xs font-medium text-gray-400 text-center">
              Best
            </div>
            <div className="text-xs font-medium text-gray-400 text-right">
              Action
            </div>
          </div>

          {exams.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-400 text-sm">
              No exams available yet.
            </div>
          ) : (
            exams.map((exam, i) => {
              const examSessions = tabSessions
                .filter((s) => s.exam_definition_id === exam.id)
                .sort(
                  (a, b) =>
                    new Date(b.started_at).getTime() -
                    new Date(a.started_at).getTime()
                );
              const attemptCount = examSessions.length;
              const scoredAttempts = examSessions.filter((s) => s.scored_at);
              const best = scoredAttempts.reduce<number | null>(
                (b, s) =>
                  s.total_score != null && (b == null || s.total_score > b)
                    ? s.total_score
                    : b,
                null
              );
              // Find the actual session row for the best score so we can link
              // directly to its report (saves the user a trip through the
              // dashboard).
              const bestSession = best != null
                ? scoredAttempts.find((s) => s.total_score === best)
                : null;
              const isOpen = expanded.has(exam.id);
              const hasInProgress = examSessions.some(
                (s) => s.status === "in_progress"
              );
              // The most recent in-progress session for this exam (newest first).
              // Used to make the top-level button "Continue" instead of always
              // creating a new session when the user already has one mid-flight.
              const inProgressSession = examSessions
                .filter(s => s.status === "in_progress")
                .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0];
              const hasScoringNow = examSessions.some(
                (s) => scoringIds.has(s.id) && !s.scored_at
              );

              return (
                <div
                  key={exam.id}
                  className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                >
                  {/* Exam row — in-progress rows get a subtle amber tint so
                      they stand out at a glance from untouched exams. */}
                  <div
                    className={`flex sm:grid sm:grid-cols-[1fr_80px_80px_140px] gap-4 items-center px-5 py-4 transition-colors
                      ${inProgressSession ? "bg-amber-50/60 dark:bg-amber-900/10" : ""}
                      ${attemptCount > 0 ? "cursor-pointer hover:bg-gray-50/70 dark:hover:bg-gray-800/40" : ""}`}
                    onClick={() =>
                      attemptCount > 0 && toggleExpanded(exam.id)
                    }
                  >
                    {/* Name */}
                    <div className="flex items-center gap-3 min-w-0">
                      {attemptCount > 0 ? (
                        <span
                          className={`text-gray-400 transition-transform text-xs flex-shrink-0 ${isOpen ? "rotate-90" : ""}`}
                        >
                          ▶
                        </span>
                      ) : (
                        <span className="w-[16px] flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                          Practice Exam #{i + 1}
                        </div>
                        {/* Section list truncates on narrow viewports — on a 375px
                            screen the full list ("Reading · Listening · Speaking
                            · Writing") wrapped to 3-4 lines and crushed the row. */}
                        <div className="text-xs text-gray-400 mt-0.5 truncate">
                          {config.sections.map((s) => s.label).join(" · ")}
                        </div>
                      </div>
                      {hasScoringNow && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 flex-shrink-0">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                          Scoring…
                        </span>
                      )}
                      {hasInProgress && !hasScoringNow && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-50 text-yellow-700 flex-shrink-0">
                          In progress
                        </span>
                      )}
                    </div>

                    {/* Attempts — hidden on mobile to give the name column
                        room (it gets crushed at 375px otherwise). */}
                    <div className="hidden sm:block text-center">
                      {attemptCount > 0 ? (
                        <span className="text-sm text-gray-500">
                          {attemptCount}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-300">—</span>
                      )}
                    </div>

                    {/* Best score — hidden on mobile, same reason. */}
                    <div className="hidden sm:block text-center">
                      {best != null ? (
                        (() => {
                          const pct = scoreRatio(examType, best, maxScore);
                          return (
                            <span
                              className={`font-bold text-sm ${pct >= 0.8 ? "text-green-600" : pct >= 0.6 ? "text-blue-600" : pct >= 0.4 ? "text-yellow-600" : "text-red-500"}`}
                            >
                              {formatTotalScore(examType, best)}
                              <span className="text-gray-300 font-normal text-xs">
                                {config.scoreLabel}
                              </span>
                            </span>
                          );
                        })()
                      ) : (
                        <span className="text-sm text-gray-300">—</span>
                      )}
                    </div>

                    {/* Action — when an in-progress session exists, show Continue
                        (primary) AND Retake (secondary) stacked, so users with a
                        stale paused session can still start a fresh attempt.
                        flex-shrink-0 + w-[110px] reserves space on mobile so the
                        buttons don't get crushed by the (truncated) name column. */}
                    <div
                      className="flex flex-col items-end gap-1 flex-shrink-0 w-[110px] sm:w-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {inProgressSession ? (
                        <>
                          <a
                            href={`/${examType}/exam/${inProgressSession.id}`}
                            className="inline-block w-full text-center text-sm text-white px-4 py-1.5 rounded-lg transition-colors font-medium bg-amber-500 hover:bg-amber-600"
                          >
                            Continue →
                          </a>
                          <button
                            onClick={() => startExam(exam.id)}
                            disabled={starting === exam.id}
                            className="w-full text-xs text-gray-600 dark:text-gray-300 px-3 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 font-medium"
                            title="Start a new attempt — your in-progress one will stay saved"
                          >
                            {starting === exam.id ? "Starting…" : "Retake"}
                          </button>
                        </>
                      ) : bestSession ? (
                        <>
                          <button
                            onClick={() => startExam(exam.id)}
                            disabled={starting === exam.id}
                            className={`w-full text-sm text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50 font-medium ${colors.active} hover:opacity-90`}
                          >
                            {starting === exam.id ? "Starting…" : "Retake"}
                          </button>
                          <a
                            href={`/${examType}/report/${bestSession.id}`}
                            className="w-full text-center text-xs text-gray-700 dark:text-gray-300 px-3 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
                            title="View the best-scoring attempt's report"
                          >
                            View report →
                          </a>
                        </>
                      ) : (
                        <button
                          onClick={() => startExam(exam.id)}
                          disabled={starting === exam.id}
                          className={`text-sm text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50 font-medium ${colors.active} hover:opacity-90`}
                        >
                          {starting === exam.id
                            ? "Starting…"
                            : attemptCount > 0
                              ? "Retake"
                              : "Start →"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Accordion: attempts */}
                  {isOpen && attemptCount > 0 && (
                    <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/30">
                      {/* Attempt sub-header */}
                      {examType === "sat" ? (
                        <div className="grid grid-cols-[1fr_90px_70px_70px_80px_110px] gap-3 px-10 py-2 border-b border-gray-100 dark:border-gray-800">
                          {["Date", "Status", "R&W", "Math", "Total", ""].map(
                            (h) => (
                              <div
                                key={h}
                                className="text-xs font-medium text-gray-400 text-center first:text-left last:text-right"
                              >
                                {h}
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <div
                          className="grid gap-3 px-10 py-2 border-b border-gray-100 dark:border-gray-800"
                          style={{ gridTemplateColumns: attemptGridTemplate }}
                        >
                          {[
                            "Date",
                            "Status",
                            ...sectionCols.map((c) => c.label),
                            "Total",
                            "",
                          ].map((h) => (
                            <div
                              key={h}
                              className="text-xs font-medium text-gray-400 text-center first:text-left last:text-right"
                            >
                              {h}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Attempt rows */}
                      {examSessions.map((s) => {
                        const isScoring =
                          scoringIds.has(s.id) && !s.scored_at;

                        return examType === "sat" ? (
                          <div
                            key={s.id}
                            className="grid grid-cols-[1fr_90px_70px_70px_80px_110px] gap-3 items-center px-10 py-3 border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-white/70 dark:hover:bg-gray-800/30 transition-colors"
                          >
                            <div className="text-xs text-gray-500">
                              {new Date(s.started_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )}
                            </div>
                            <div className="text-center">
                              <AttemptStatusBadge
                                session={s}
                                scoringIds={scoringIds}
                              />
                            </div>
                            <div className="text-center">
                              <ScorePill
                                score={
                                  (s.feedback_report?.rw_score as number | undefined) ??
                                  s.reading_score
                                }
                                max={800}
                              />
                            </div>
                            <div className="text-center">
                              <ScorePill
                                score={
                                  (s.feedback_report?.math_score as number | undefined) ??
                                  s.listening_score
                                }
                                max={800}
                              />
                            </div>
                            <div className="text-center">
                              {s.total_score != null ? (
                                <span className="font-bold text-sm text-gray-700 dark:text-gray-300">
                                  {formatTotalScore(examType, s.total_score)}
                                  <span className="text-gray-300 font-normal text-xs">
                                    {config.scoreLabel}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-sm text-gray-300">
                                  —
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-end gap-2">
                              {s.status === "in_progress" && (
                                <Link
                                  href={`/${s.exam_type}/exam/${s.id}`}
                                  className="text-xs bg-yellow-500 text-white px-3 py-1 rounded-md hover:bg-yellow-600 transition-colors font-medium"
                                >
                                  Continue →
                                </Link>
                              )}
                              {s.status === "completed" &&
                                !s.scored_at &&
                                !isScoring && (
                                  <button
                                    onClick={() => scoreSession(s.id)}
                                    className="text-xs text-blue-600 hover:underline font-medium"
                                  >
                                    Score
                                  </button>
                                )}
                              {isScoring && (
                                <span className="text-xs text-blue-400 font-medium">
                                  Scoring…
                                </span>
                              )}
                              {s.scored_at && (
                                <Link
                                  href={`/${s.exam_type}/report/${s.id}`}
                                  className="text-xs text-blue-600 hover:underline font-medium"
                                >
                                  Report
                                </Link>
                              )}
                              <button
                                onClick={() => deleteSession(s.id)}
                                disabled={deletingId === s.id}
                                title="Delete attempt"
                                className="text-xs text-gray-300 hover:text-red-500 transition-colors"
                              >
                                {deletingId === s.id ? "…" : "✕"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            key={s.id}
                            className="grid gap-3 items-center px-10 py-3 border-b border-gray-50 dark:border-gray-800/50 last:border-0 hover:bg-white/70 dark:hover:bg-gray-800/30 transition-colors"
                            style={{ gridTemplateColumns: attemptGridTemplate }}
                          >
                            <div className="text-xs text-gray-500">
                              {new Date(s.started_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )}
                            </div>
                            <div className="text-center">
                              <AttemptStatusBadge
                                session={s}
                                scoringIds={scoringIds}
                              />
                            </div>
                            {sectionCols.map((col) => {
                              const colKey =
                                col.source === "column" ? col.key : `bd_${col.sectionId}`;
                              return (
                                <div key={colKey} className="text-center">
                                  <ScorePill
                                    examType={examType}
                                    score={getSessionSectionScore(s, col)}
                                    max={col.scoreMax}
                                  />
                                </div>
                              );
                            })}
                            <div className="text-center">
                              {s.total_score != null ? (
                                <span className="font-bold text-sm text-gray-700 dark:text-gray-300">
                                  {formatTotalScore(examType, s.total_score)}
                                  <span className="text-gray-300 font-normal text-xs">
                                    {config.scoreLabel}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-sm text-gray-300">
                                  —
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-end gap-2">
                              {s.status === "in_progress" && (
                                <Link
                                  href={`/${s.exam_type}/exam/${s.id}`}
                                  className="text-xs bg-yellow-500 text-white px-3 py-1 rounded-md hover:bg-yellow-600 transition-colors font-medium"
                                >
                                  Continue →
                                </Link>
                              )}
                              {s.status === "completed" &&
                                !s.scored_at &&
                                !isScoring && (
                                  <button
                                    onClick={() => scoreSession(s.id)}
                                    className="text-xs text-blue-600 hover:underline font-medium"
                                  >
                                    Score
                                  </button>
                                )}
                              {isScoring && (
                                <span className="text-xs text-blue-400 font-medium">
                                  Scoring…
                                </span>
                              )}
                              {s.scored_at && (
                                <Link
                                  href={`/${s.exam_type}/report/${s.id}`}
                                  className="text-xs text-blue-600 hover:underline font-medium"
                                >
                                  Report
                                </Link>
                              )}
                              <button
                                onClick={() => deleteSession(s.id)}
                                disabled={deletingId === s.id}
                                title="Delete attempt"
                                className="text-xs text-gray-300 hover:text-red-500 transition-colors"
                              >
                                {deletingId === s.id ? "…" : "✕"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Onboarding banner for first-time users */}
        {exams.length > 0 && tabSessions.length === 0 && (
          <div className={`mt-6 ${colors.lightBg} border ${colors.border} rounded-xl p-6`}>
            <div className="flex items-start gap-4">
              <GraduationCap className={`w-6 h-6 flex-shrink-0 ${colors.text}`} strokeWidth={1.75} aria-hidden="true" />
              <div>
                <div className={`font-semibold ${colors.text} mb-1`}>
                  Start your first free practice exam
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Practice Exam #1 is free — no payment needed. You&apos;ll get a
                  full {config.sections.map((s) => s.label).join(", ")} session
                  followed by AI scoring, detailed question-by-question feedback,
                  and a personalized score report.
                </p>
                <div className="flex flex-wrap gap-3 text-sm text-gray-700 dark:text-gray-300">
                  {[
                    `~${Math.round(config.sections.reduce((sum, s) => sum + s.duration, 0) / 60 * 10) / 10} hours total`,
                    "AI graded",
                    "Detailed score report",
                    "Question review",
                  ].map((f) => (
                    <span key={f} className="flex items-center gap-1">
                      <span className={colors.text}>✓</span>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) return mainContent;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Back link */}
        <Link
          href="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          ← Back to Dashboard
        </Link>
        {mainContent}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Exported wrapper with Suspense                                     */
/* ------------------------------------------------------------------ */

export default function PracticePage({ examType, embedded }: PracticePageProps) {
  const [scoringParam, setScoringParam] = useState<string | null>(null);
  return (
    <>
      {/* Only this invisible bridge suspends (useSearchParams) — the page
          itself renders immediately, so the old full-page "Loading…" hang
          under the Turbopack dev server can't occur. */}
      <Suspense fallback={null}>
        <ScoringParamBridge onParam={setScoringParam} />
      </Suspense>
      <PracticePageContent examType={examType} embedded={embedded} scoringParam={scoringParam} />
    </>
  );
}
